'use client';
import Link from '@/components/ui/Link';

interface BlogPostProps {
    image: string;
    date: string;
    title: string;
    href: string;
}

const BlogPostCard = ({ image, date, title, href }: BlogPostProps) => (
    <div className="border border-border-subtle rounded-xl sm:rounded-2xl lg:rounded-[20px] p-4 sm:p-6 lg:p-[26px] flex flex-col gap-4 sm:gap-5 lg:gap-[20px] hover:shadow-lg transition-shadow duration-200">
        <div className="relative overflow-hidden rounded-lg sm:rounded-xl lg:rounded-[12px]">
            <img
                src={image}
                alt={title}
                loading="lazy"
                decoding="async"
                className="w-full h-[200px] sm:h-[260px] lg:h-[294px] object-cover hover:scale-105 transition-transform duration-300"
            />
        </div>

        <div className="flex flex-col gap-4 sm:gap-5 lg:gap-6">
            <p className="text-base sm:text-lg lg:text-lg text-text-muted font-semibold leading-5 sm:leading-6 lg:leading-md">
                {date}
            </p>
            <h3 className="text-xl sm:text-2xl lg:text-4xl text-text-primary font-bold leading-6 sm:leading-7 lg:leading-7xl">
                {title}
            </h3>

            <Link
                href={href}
                className="flex flex-row items-center gap-2 group"
                ariaLabel={`Read more about ${title}`}
            >
                <span className="text-sm sm:text-base lg:text-base text-text-muted font-semibold leading-4 sm:leading-5 lg:leading-base group-hover:text-primary-brand transition-colors duration-200">
                    Read More
                </span>
                <img
                    src="/images/img_arrow_right_gray_600.svg"
                    alt=""
                    className="w-3 h-3 sm:w-4 sm:h-4 lg:w-[16px] lg:h-[16px] group-hover:translate-x-1 transition-transform duration-200"
                />
            </Link>
        </div>
    </div>
)

export default function BlogSection() {
    const blogPosts: BlogPostProps[] = [
        {
            image: '/images/img_rectangle_92.webp',
            date: 'May 20, 2026',
            title: '5 Summer Fashion Trends You Need to Know',
            href: '/blog/summer-fashion-trends'
        },
        {
            image: '/images/img_rectangle_93.webp',
            date: 'May 20, 2026',
            title: '5 Summer Fashion Trends You Need to Know',
            href: '/blog/summer-fashion-trends-2'
        },
        {
            image: '/images/img_rectangle_94.webp',
            date: 'May 20, 2026',
            title: '5 Summer Fashion Trends You Need to Know',
            href: '/blog/summer-fashion-trends-3'
        }
    ]

    return (
        <section className="w-full pt-4 sm:pt-6 lg:pt-8 pb-12 sm:pb-16 lg:pb-20">
            <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                <div className="w-full max-w-[1804px] mx-auto flex flex-col gap-6 sm:gap-8 lg:gap-[36px]">
                    {/* Section Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <h2 className="text-3xl sm:text-4xl lg:text-8xl text-text-primary font-semibold leading-9 sm:leading-10 lg:leading-11xl">
                            Latest From Our Blog
                        </h2>

                        <Link
                            href="/blog"
                            className="flex flex-row items-center gap-2 sm:gap-3 lg:gap-[14px] group"
                            ariaLabel="View all blog posts"
                        >
                            <span className="text-xl sm:text-2xl lg:text-5xl text-text-subtle font-medium leading-6 sm:leading-7 lg:leading-7xl group-hover:text-primary-brand transition-colors duration-200">
                                View all
                            </span>
                            <img
                                src="/images/img_arrow_right_gray_600_01.svg"
                                alt=""
                                className="w-3 h-3 sm:w-4 sm:h-4 lg:w-[16px] lg:h-[16px] group-hover:translate-x-1 transition-transform duration-200"
                            />
                        </Link>
                    </div>

                    {/* Blog Posts Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-[30px]">
                        {blogPosts.map((post, index) => (
                            <BlogPostCard key={index} {...post} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}